import { useMemo } from 'react';
import { safeImageSrc } from '../../lib/safeUrl';
import { Link, useNavigate } from '@tanstack/react-router';
import { Group, Stack, Text, Title } from '@mantine/core';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock,
  FileText,
  GraduationCap,
  TerminalSquare,
  Target,
} from 'lucide-react';
import { Badge, Button, Card, Pill } from '../ui';
import { colors } from '../../theme/tokens';
import type { LessonViewData } from '../../hooks/useLessons';

/**
 * LessonView — Phase 4.5 course-module reader (PLAN.md §Phase 4.5).
 *
 * Renders the body of one course module: theory text (markdown or plain),
 * optional embedded PDF, and cross-links to the linked lab and/or scenario.
 * Includes prev/next navigation across the parent course's module list and
 * a "Mark complete" CTA wired to `useMarkLessonComplete`.
 *
 * The markdown renderer is intentionally minimal (no external dep) — it
 * covers the subset our authoring guide produces today (headings, paragraphs,
 * lists, fenced code blocks, inline code, bold, links). When richer
 * formatting is required, swap to `react-markdown` behind a dynamic import.
 */

export interface LessonViewProps {
  data: LessonViewData;
  onMarkComplete: () => void;
  isCompleting: boolean;
  completeError?: string | null;
}

export function LessonView({ data, onMarkComplete, isCompleting, completeError }: LessonViewProps) {
  const { course, module: mod, position, total, body, prev, next, progress } = data;
  const isCompleted = progress.status === 'completed';

  return (
    <Stack gap="lg">
      <LessonHeader
        course={course}
        title={mod.title}
        position={position}
        total={total}
        durationMins={mod.durationMins}
        completed={isCompleted}
      />

      <Group align="flex-start" gap="lg" wrap="wrap">
        <Stack gap="lg" style={{ flex: '1 1 540px', minWidth: 0 }}>
          <Card>{renderBody(body)}</Card>
          {completeError ? (
            <Card>
              <Text size="sm" c={colors.hudRed}>
                {completeError}
              </Text>
            </Card>
          ) : null}
        </Stack>

        <Stack gap="md" style={{ width: 320, flex: '0 0 320px' }}>
          {mod.scenarioSlug ? <ScenarioLink slug={mod.scenarioSlug} /> : null}
          {mod.labId ? <LabLink id={mod.labId} /> : null}
          <ProgressCard progress={progress} />
        </Stack>
      </Group>

      <LessonFooter
        courseSlug={course.slug}
        prev={prev}
        next={next}
        completed={isCompleted}
        isCompleting={isCompleting}
        onMarkComplete={onMarkComplete}
      />
    </Stack>
  );
}

// ---------- header ----------

interface LessonHeaderProps {
  course: { slug: string; title: string };
  title: string;
  position: number;
  total: number;
  durationMins: number;
  completed: boolean;
}

function LessonHeader({ course, title, position, total, durationMins, completed }: LessonHeaderProps) {
  return (
    <Stack gap={4}>
      <Group gap="xs" align="center">
        <Link
          to="/courses/$id"
          params={{ id: course.slug }}
          preload="intent"
          style={{
            textDecoration: 'none',
            color: 'rgb(var(--c-txt-muted))',
            fontSize: 12,
          }}
        >
          {course.title}
        </Link>
        <Text size="xs" c="dimmed">
          ›
        </Text>
        <Text size="xs" c="dimmed">
          Lesson {position} of {total}
        </Text>
      </Group>
      <Group justify="space-between" align="end" wrap="wrap">
        <Group gap="sm" align="center">
          <Title order={1}>
            {title}
          </Title>
          {completed ? (
            <Pill variant="success" size="sm">
              completed
            </Pill>
          ) : (
            <Pill variant="info" size="sm">
              in progress
            </Pill>
          )}
        </Group>
        <Group gap="md">
          <Group gap={6}>
            <Clock size={14} color={colors.hudCyan} />
            <Text size="xs" c="dimmed">
              {durationMins} min
            </Text>
          </Group>
        </Group>
      </Group>
    </Stack>
  );
}

// ---------- body ----------

function renderBody(body: LessonViewData['body']) {
  if (body.format === 'pdf' && body.pdfUrl) {
    return (
      <Stack gap="sm">
        <Group gap={6}>
          <FileText size={14} color={colors.hudCyan} />
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            PDF lesson
          </Text>
        </Group>
        <iframe
          src={body.pdfUrl}
          title="Lesson PDF"
          style={{
            width: '100%',
            height: 720,
            border: '1px solid var(--c-bd)',
            borderRadius: 8,
            background: 'rgb(var(--c-panel))',
          }}
        />
      </Stack>
    );
  }
  if (body.format === 'text') {
    return (
      <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
        {body.source}
      </Text>
    );
  }
  return <MarkdownBody source={body.source} />;
}

export function MarkdownBody({ source }: { source: string }) {
  // Memoise the parse so React doesn't redo it on every render (the parent
  // re-renders on mutation state changes). Per Performance skill.
  const blocks = useMemo(() => parseMarkdown(source), [source]);
  return (
    <Stack gap="sm">
      {blocks.map((block, i) => (
        <RenderBlock key={i} block={block} />
      ))}
    </Stack>
  );
}

// ---------- cross-links ----------

function LabLink({ id }: { id: string }) {
  return (
    <Card>
      <Stack gap="xs">
        <Group gap={6}>
          <TerminalSquare size={14} color={colors.hudCyan} />
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Linked lab
          </Text>
        </Group>
        <Text ff="JetBrains Mono" size="sm" c="rgb(var(--c-txt-bright))" fw={600}>
          {id}
        </Text>
        <Link
          to="/labs/$id"
          params={{ id }}
          preload="intent"
          style={{ textDecoration: 'none' }}
        >
          <Button variant="secondary" size="xs" leftIcon={<TerminalSquare size={12} />}>
            Open lab
          </Button>
        </Link>
      </Stack>
    </Card>
  );
}

function ScenarioLink({ slug }: { slug: string }) {
  return (
    <Card>
      <Stack gap="xs">
        <Group gap={6}>
          <Target size={14} color={colors.hudAmber} />
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Linked scenario
          </Text>
        </Group>
        <Text ff="JetBrains Mono" size="sm" c="rgb(var(--c-txt-bright))" fw={600}>
          {slug}
        </Text>
        <Link
          to="/scenarios/$slug"
          params={{ slug }}
          preload="intent"
          style={{ textDecoration: 'none' }}
        >
          <Button variant="secondary" size="xs" leftIcon={<Target size={12} />}>
            Open scenario
          </Button>
        </Link>
      </Stack>
    </Card>
  );
}

function ProgressCard({ progress }: { progress: LessonViewData['progress'] }) {
  return (
    <Card>
      <Stack gap="xs">
        <Group gap={6}>
          <GraduationCap size={14} color={colors.neon} />
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Your progress
          </Text>
        </Group>
        <Group gap="sm">
          <Badge variant={progressVariant(progress.status)}>
            {progress.status.replace('_', ' ')}
          </Badge>
          {progress.completedAt ? (
            <Text size="xs" c="dimmed" ff="JetBrains Mono">
              {new Date(progress.completedAt).toLocaleString()}
            </Text>
          ) : null}
        </Group>
      </Stack>
    </Card>
  );
}

function progressVariant(s: LessonViewData['progress']['status']): 'success' | 'info' | 'warn' {
  if (s === 'completed') return 'success';
  if (s === 'in_progress') return 'info';
  return 'warn';
}

// ---------- footer nav ----------

interface LessonFooterProps {
  courseSlug: string;
  prev: LessonViewData['prev'];
  next: LessonViewData['next'];
  completed: boolean;
  isCompleting: boolean;
  onMarkComplete: () => void;
}

function LessonFooter({ courseSlug, prev, next, completed, isCompleting, onMarkComplete }: LessonFooterProps) {
  const navigate = useNavigate();
  const goTo = (moduleId: string) =>
    navigate({ to: '/courses/$slug/$module', params: { slug: courseSlug, module: moduleId } });

  return (
    <Group justify="space-between" wrap="wrap" gap="sm">
      <Group gap="sm">
        {prev ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => goTo(prev.id)}
            leftIcon={<ArrowLeft size={14} />}
          >
            {truncate(prev.title, 28)}
          </Button>
        ) : (
          <span />
        )}
      </Group>
      <Group gap="sm">
        <Button
          variant={completed ? 'secondary' : 'primary'}
          size="sm"
          loading={isCompleting}
          onClick={onMarkComplete}
          leftIcon={<CheckCircle2 size={14} />}
          disabled={completed}
        >
          {completed ? 'Completed' : 'Mark complete'}
        </Button>
        {next ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => goTo(next.id)}
            rightIcon={<ArrowRight size={14} />}
          >
            {truncate(next.title, 28)}
          </Button>
        ) : null}
      </Group>
    </Group>
  );
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}

// ---------- minimal markdown ----------

type MdBlock =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'blockquote'; text: string }
  | { kind: 'code'; lang: string; body: string }
  | { kind: 'image'; src: string; alt: string }
  | { kind: 'table'; headers: string[]; rows: string[][] };

/**
 * Tiny markdown -> block parser. Pure, side-effect-free.
 * Covers headings (1-3), paragraphs, unordered/ordered lists,
 * blockquotes, and fenced code blocks. Inline rules (bold, code,
 * links) are handled at render time by `renderInline`. Kept
 * file-local; extract to a sibling helper if unit tests are added.
 */
function parseMarkdown(source: string): MdBlock[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: MdBlock[] = [];
  let i = 0;
  const at = (n: number): string => lines[n] ?? '';
  while (i < lines.length) {
    const line = at(i);
    // fenced code
    const fence = line.match(/^```(\w*)\s*$/);
    if (fence) {
      const lang = fence[1] ?? '';
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(at(i))) {
        buf.push(at(i));
        i++;
      }
      i++; // consume closing fence
      blocks.push({ kind: 'code', lang, body: buf.join('\n') });
      continue;
    }
    // standalone image: ![alt](url) on its own line
    const img = line.match(/^\s*!\[([^\]]*)\]\(([^)]+)\)\s*$/);
    if (img && img[2]) {
      blocks.push({ kind: 'image', alt: (img[1] ?? '').trim(), src: img[2].trim() });
      i++;
      continue;
    }
    // heading
    const h = line.match(/^(#{1,3})\s+(.+)$/);
    if (h && h[1] && h[2]) {
      blocks.push({ kind: 'heading', level: h[1].length as 1 | 2 | 3, text: h[2].trim() });
      i++;
      continue;
    }
    // unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(at(i))) {
        items.push(at(i).replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }
    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(at(i))) {
        items.push(at(i).replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }
    // table: a `| header |` row immediately followed by a `|---|---|` separator
    if (/^\s*\|.*\|\s*$/.test(line) && /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(at(i + 1))) {
      const splitRow = (s: string): string[] =>
        s.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
      const headers = splitRow(line);
      i += 2; // consume the header + the separator
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(at(i))) {
        rows.push(splitRow(at(i)));
        i++;
      }
      blocks.push({ kind: 'table', headers, rows });
      continue;
    }
    // blockquote
    if (/^>\s?/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^>\s?/.test(at(i))) {
        buf.push(at(i).replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({ kind: 'blockquote', text: buf.join(' ') });
      continue;
    }
    // blank line — skip
    if (line.trim() === '') {
      i++;
      continue;
    }
    // paragraph — collect contiguous non-empty, non-special lines
    const buf: string[] = [line];
    i++;
    while (
      i < lines.length &&
      at(i).trim() !== '' &&
      !/^(#{1,3})\s+/.test(at(i)) &&
      !/^\s*[-*]\s+/.test(at(i)) &&
      !/^\s*\d+\.\s+/.test(at(i)) &&
      !/^>\s?/.test(at(i)) &&
      !/^```/.test(at(i)) &&
      !/^\s*\|.*\|\s*$/.test(at(i))
    ) {
      buf.push(at(i));
      i++;
    }
    blocks.push({ kind: 'paragraph', text: buf.join(' ') });
  }
  return blocks;
}

function RenderBlock({ block }: { block: MdBlock }) {
  switch (block.kind) {
    case 'heading': {
      const order = block.level as 1 | 2 | 3;
      const size = order === 1 ? '24px' : order === 2 ? '18px' : '14px';
      return (
        <Title order={order} c="rgb(var(--c-txt-bright))" style={{ fontSize: size, marginTop: 8 }}>
          {renderInline(block.text)}
        </Title>
      );
    }
    case 'paragraph':
      return (
        <Text size="sm" c="dimmed" lh={1.6}>
          {renderInline(block.text)}
        </Text>
      );
    case 'ul':
      return (
        <ul style={{ margin: 0, paddingLeft: 20, color: 'rgb(var(--c-txt-2))' }}>
          {block.items.map((it, i) => (
            <li key={i} style={{ fontSize: 13, lineHeight: 1.6 }}>
              {renderInline(it)}
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol style={{ margin: 0, paddingLeft: 20, color: 'rgb(var(--c-txt-2))' }}>
          {block.items.map((it, i) => (
            <li key={i} style={{ fontSize: 13, lineHeight: 1.6 }}>
              {renderInline(it)}
            </li>
          ))}
        </ol>
      );
    case 'blockquote':
      return (
        <div
          style={{
            borderLeft: `3px solid ${colors.neon}`,
            padding: '4px 12px',
            background: 'rgb(var(--c-panel))',
            borderRadius: 4,
          }}
        >
          <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>
            {renderInline(block.text)}
          </Text>
        </div>
      );
    case 'table':
      return (
        <div style={{ overflowX: 'auto', margin: '10px 0' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                {block.headers.map((h, i) => (
                  <th
                    key={i}
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      borderBottom: '2px solid rgb(var(--c-bd-bright))',
                      color: 'rgb(var(--c-txt-bright))',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {renderInline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((r, ri) => (
                <tr key={ri} style={{ background: ri % 2 ? 'rgb(var(--c-panel))' : 'transparent' }}>
                  {r.map((c, ci) => (
                    <td
                      key={ci}
                      style={{
                        padding: '8px 12px',
                        borderBottom: '1px solid rgb(var(--c-bd))',
                        color: 'rgb(var(--c-txt-2))',
                        verticalAlign: 'top',
                        lineHeight: 1.5,
                      }}
                    >
                      {renderInline(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'image': {
      const safeSrc = safeImageSrc(block.src);
      if (!safeSrc) return null;
      return (
        <figure style={{ margin: '4px 0' }}>
          <img
            src={safeSrc}
            alt={block.alt}
            loading="lazy"
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 8,
              border: '1px solid var(--c-bd)',
              display: 'block',
            }}
          />
          {block.alt ? (
            <figcaption style={{ fontSize: 12, color: 'rgb(var(--c-txt-2))', marginTop: 4, textAlign: 'center' }}>
              {block.alt}
            </figcaption>
          ) : null}
        </figure>
      );
    }
    case 'code':
      return (
        <pre
          style={{
            margin: 0,
            padding: 12,
            background: 'rgb(var(--c-panel))',
            border: '1px solid var(--c-bd)',
            borderRadius: 6,
            overflow: 'auto',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 12,
            color: colors.neon,
          }}
          aria-label={block.lang ? `${block.lang} code block` : 'code block'}
        >
          <code>{block.body}</code>
        </pre>
      );
  }
}

// Inline rules: **bold**, `code`, [text](url). Applied in fixed order against
// a tokenised string so nesting is well-defined (bold can contain code but
// not the other way around). Each token becomes a React fragment child.
function renderInline(input: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  // Regex captures one rule at a time, left-to-right, non-greedy.
  const rx = /(\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = rx.exec(input)) !== null) {
    if (m.index > last) {
      out.push(input.slice(last, m.index));
    }
    if (m[2] !== undefined) {
      out.push(
        <strong key={key++} style={{ color: 'rgb(var(--c-txt-bright))' }}>
          {m[2]}
        </strong>,
      );
    } else if (m[3] !== undefined) {
      out.push(
        <code
          key={key++}
          style={{
            background: 'rgb(var(--c-panel))',
            color: colors.hudCyan,
            padding: '1px 6px',
            borderRadius: 3,
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '0.92em',
          }}
        >
          {m[3]}
        </code>,
      );
    } else if (m[4] !== undefined && m[5] !== undefined) {
      // Only allow http(s) and relative URLs — refuse javascript: etc.
      const safe = isSafeHref(m[5]) ? m[5] : '#';
      out.push(
        <a
          key={key++}
          href={safe}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: colors.hudCyan, textDecoration: 'underline' }}
        >
          {m[4]}
        </a>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < input.length) {
    out.push(input.slice(last));
  }
  return out;
}

function isSafeHref(href: string): boolean {
  const trimmed = href.trim().toLowerCase();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return true;
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('mailto:')) return true;
  return false;
}
